import type { AdminUser, UpdateUserRequest } from '@mcgames/contracts';
import { Badge, Button, Spinner } from '@mcgames/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../api/auth-store.ts';
import { useApiMutation, useApiQuery } from '../api/hooks.ts';
import { formatDateTime } from '../lib/format-date.ts';
import styles from './Admin.module.css';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { InvitesSection } from './InvitesSection.tsx';

const USERS_PATH = '/api/admin/users';

export function UsersPage() {
  const { t } = useTranslation('admin');
  const session = useSession();
  const users = useApiQuery<AdminUser[]>(USERS_PATH);

  return (
    <div className={styles['page']}>
      <h1 className={styles['heading']}>{t('users.title')}</h1>
      {users.isPending && <Spinner />}
      {users.isError && (
        <p role="alert" className={styles['error']}>
          {t('users.loadFailed')}
        </p>
      )}
      {users.data?.length === 0 && <p className={styles['muted']}>{t('users.empty')}</p>}
      {users.data !== undefined && users.data.length > 0 && (
        <div className={styles['tableWrap']}>
          <table className={styles['table']} aria-label={t('users.label')}>
            <thead>
              <tr>
                <th scope="col">{t('users.username')}</th>
                <th scope="col">{t('users.role')}</th>
                <th scope="col">{t('users.mfa')}</th>
                <th scope="col">{t('users.status')}</th>
                <th scope="col">{t('users.lastLogin')}</th>
                <th scope="col">{t('users.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {users.data.map((user) => (
                <UserRow key={user.id} user={user} isSelf={user.id === session.user?.id} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      <InvitesSection />
    </div>
  );
}

type Asking = 'toggle' | 'role' | 'resetMfa';

function UserRow({ user, isSelf }: { user: AdminUser; isSelf: boolean }) {
  const { t, i18n } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState<Asking | null>(null);
  const userPath = `${USERS_PATH}/${encodeURIComponent(user.id)}`;
  const done = () => {
    setAsking(null);
    void queryClient.invalidateQueries({ queryKey: [USERS_PATH] });
  };
  const toggle = useApiMutation<UpdateUserRequest, AdminUser>('PATCH', userPath, {
    onSuccess: done,
  });
  const changeRole = useApiMutation<UpdateUserRequest, AdminUser>('PATCH', userPath, {
    onSuccess: done,
  });
  const resetMfa = useApiMutation<void>('POST', `${userPath}/mfa-reset`, { onSuccess: done });

  function ask(next: Asking) {
    toggle.reset();
    changeRole.reset();
    resetMfa.reset();
    setAsking(next);
  }

  const key = user.disabled ? 'enable' : 'disable';
  const roleKey = user.role === 'admin' ? 'demote' : 'promote';
  return (
    <tr>
      <td className={styles['wrap']}>
        {user.username}
        {isSelf && <span className={styles['muted']}> {t('users.you')}</span>}
      </td>
      <td>
        <Badge tone={user.role === 'admin' ? 'accent' : 'default'}>
          {t(`users.roles.${user.role}`)}
        </Badge>
      </td>
      <td>
        <Badge tone={user.mfaEnabled ? 'accent' : 'default'}>
          {user.mfaEnabled ? t('users.mfaOn') : t('users.mfaOff')}
        </Badge>
      </td>
      <td>
        <Badge tone={user.disabled ? 'danger' : 'default'}>
          {user.disabled ? t('users.disabled') : t('users.active')}
        </Badge>
      </td>
      <td className={styles['nowrap']}>
        {user.lastLoginAt === null
          ? t('users.never')
          : formatDateTime(user.lastLoginAt, i18n.language)}
      </td>
      <td>
        <div className={styles['actions']}>
          {!isSelf && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => ask('toggle')}
              aria-label={t(`users.${key}`, { username: user.username })}
            >
              {t(`users.confirm.${key}Confirm`)}
            </Button>
          )}
          {!isSelf && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => ask('role')}
              aria-label={t(`users.${roleKey}`, { username: user.username })}
            >
              {t(`users.confirm.${roleKey}Confirm`)}
            </Button>
          )}
          {user.mfaEnabled && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => ask('resetMfa')}
              aria-label={t('users.resetMfa', { username: user.username })}
            >
              {t('users.confirm.resetMfaConfirm')}
            </Button>
          )}
        </div>
        {asking === 'toggle' && (
          <ConfirmDialog
            title={t(`users.confirm.${key}Title`)}
            message={t(`users.confirm.${key}Message`, { username: user.username })}
            confirmLabel={t(`users.confirm.${key}Confirm`)}
            tone={user.disabled ? 'primary' : 'danger'}
            pending={toggle.isPending}
            error={toggle.error}
            onConfirm={() => toggle.mutate({ disabled: !user.disabled })}
            onCancel={() => setAsking(null)}
          />
        )}
        {asking === 'role' && (
          <ConfirmDialog
            title={t(`users.confirm.${roleKey}Title`)}
            message={t(`users.confirm.${roleKey}Message`, { username: user.username })}
            confirmLabel={t(`users.confirm.${roleKey}Confirm`)}
            tone={user.role === 'admin' ? 'danger' : 'primary'}
            pending={changeRole.isPending}
            error={changeRole.error}
            onConfirm={() => changeRole.mutate({ role: user.role === 'admin' ? 'user' : 'admin' })}
            onCancel={() => setAsking(null)}
          />
        )}
        {asking === 'resetMfa' && (
          <ConfirmDialog
            title={t('users.confirm.resetMfaTitle')}
            message={t('users.confirm.resetMfaMessage', { username: user.username })}
            confirmLabel={t('users.confirm.resetMfaConfirm')}
            pending={resetMfa.isPending}
            error={resetMfa.error}
            onConfirm={() => resetMfa.mutate()}
            onCancel={() => setAsking(null)}
          />
        )}
      </td>
    </tr>
  );
}
